/**
 * The screen types and reads time as "mm:ss"; the API stores decimal minutes. These two
 * functions are the only crossing between the formats, and they mirror the rules of
 * `apps/server/src/modules/sequencias/utils/tempo.util.ts` on purpose: the web never imports
 * from the server, so a suggestion built there and a time typed here must still agree.
 */
const SECONDS_PER_MINUTE = 60;
const MMSS_PARTS = 2;
const MMSS_PAD = 2;
const MMSS_ZERO = '00:00';
/** Four decimals, like the server, so repeated round trips do not drift on floating point. */
const MINUTES_PRECISION = 10_000;

/** Empty or malformed becomes zero instead of NaN; a bare "3" reads as three minutes. */
export function parseMmssToMinutes(value: string): number {
  const trimmed = value.trim();
  if (trimmed === '') {
    return 0;
  }

  const parts = trimmed.split(':');
  if (parts.length > MMSS_PARTS) {
    return 0;
  }

  const minutes = Number(parts[0] ?? '');
  const seconds = parts.length === MMSS_PARTS ? Number(parts[1] ?? '') : 0;
  if (!Number.isFinite(minutes) || !Number.isFinite(seconds)) {
    return 0;
  }

  const total = minutes + seconds / SECONDS_PER_MINUTE;
  return Math.round(total * MINUTES_PRECISION) / MINUTES_PRECISION;
}

/** Decimal minutes to "mm:ss"; minutes above 99 keep every digit (`150:00`). */
export function formatMinutesAsMmss(minutos: number): string {
  if (!Number.isFinite(minutos) || minutos <= 0) {
    return MMSS_ZERO;
  }

  const totalSeconds = Math.round(minutos * SECONDS_PER_MINUTE);
  const minutes = Math.floor(totalSeconds / SECONDS_PER_MINUTE);
  const seconds = totalSeconds % SECONDS_PER_MINUTE;
  return `${String(minutes).padStart(MMSS_PAD, '0')}:${String(seconds).padStart(MMSS_PAD, '0')}`;
}

/** Keeps the field typable: digits and a single colon, so "02:15" survives mid-typing. */
export function sanitizeMmssInput(value: string): string {
  const cleaned = value.replace(/[^\d:]/g, '');
  const [head = '', ...rest] = cleaned.split(':');
  if (rest.length === 0) {
    return head;
  }
  return `${head}:${rest.join('')}`;
}
